import { type ReactNode } from 'react'
import { type LocationId, isAttachmentId } from '@terros/common'
import { type InputWrapperProps, Input } from '@mantine/core'
import { useTranslation, useUploadAttachment } from '@front/model'
import { TAlert } from '../terros'
import { TButton } from '../buttons'
import { AttachmentListItem } from './AttachmentListItem'
import { AttachmentDropzone } from './AttachmentDropzone'

type AttachmentInputProps = {
  value: string
  onChange: (value: string) => void
  locationId?: LocationId
  disabled?: boolean
} & Pick<InputWrapperProps, 'error' | 'label' | 'description' | 'withAsterisk' | 'required'>

export const AttachmentInput = ({
  value,
  onChange,
  locationId,
  disabled,
  ...props
}: AttachmentInputProps): ReactNode => {
  const { t } = useTranslation()
  const { upload, isUploading, error } = useUploadAttachment()

  if (isAttachmentId(value)) {
    return (
      <Input.Wrapper {...props}>
        <AttachmentListItem
          attachmentId={value}
          detailBold
          rightSection={
            <TButton
              tooltip='Remove attachment'
              icon='delete'
              variant='destructive'
              buttonType='icon-full'
              size='small'
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation()
                onChange('')
              }}
            />
          }
        />
      </Input.Wrapper>
    )
  }

  if (disabled) {
    return (
      <Input.Wrapper {...props}>
        <TAlert>{t('No attachment found')}</TAlert>
      </Input.Wrapper>
    )
  }

  const handleDrop = async (files: File[]): Promise<void> => {
    const attachmentId = await upload({ file: files[0], locationId })
    onChange(attachmentId)
  }

  return (
    <Input.Wrapper {...props}>
      <AttachmentDropzone loading={isUploading} error={error} onDrop={handleDrop} />
    </Input.Wrapper>
  )
}
